import { useRef, useState } from "react";
import axios from "axios";
import { motion, useReducedMotion } from "framer-motion";
import { Check, Loader2, MailCheck, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CONTACT_SECTION } from "@/content/landing";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FIELD_LABEL = "font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground";

const INTERESTS = [
  { value: "walkthrough", label: "Book a walkthrough" },
  { value: "sales", label: "Talk to sales" },
  { value: "general", label: "General enquiry" },
  { value: "press", label: "Press / media" },
];

export function SalesContactForm() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "", interest: "walkthrough" });
  const [hp, setHp] = useState("");
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const mountedAt = useRef(Date.now());

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (pending) return;
    setError("");
    setPending(true);
    try {
      const { data } = await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim(),
        message: form.message.trim(),
        interest: form.interest,
        source: "landing",
        origin: window.location.origin,
        company_website: hp,
        elapsed_ms: Date.now() - mountedAt.current,
      });
      if (data.status === "received") setDone(true);
      else setError("Unable to submit at this time. Please try again later.");
    } catch (err) {
      setError(err.response?.data?.detail || "Unable to submit at this time. Please try again later.");
    } finally {
      setPending(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="contact-form-success"
        className="rounded-3xl border border-border/60 bg-card/60 p-8 backdrop-blur"
      >
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-full sema-cta-gradient text-white">
          <MailCheck className="h-5 w-5" strokeWidth={2.2} />
        </div>
        <h3 className="mt-4 font-display text-xl font-semibold tracking-tight">Message sent — thank you.</h3>
        <p className="mt-2 max-w-md font-body text-sm leading-relaxed text-muted-foreground">
          We've received your note and emailed you a confirmation at{" "}
          <span className="font-mono text-foreground">{form.email}</span>. Our team will reply within one business day.
        </p>
      </div>
    );
  }

  return (
    <form
      data-testid="contact-form"
      onSubmit={submit}
      className="rounded-3xl border border-border/60 bg-card/60 p-7 backdrop-blur sm:p-8"
    >
      <h3 className="font-display text-xl font-semibold tracking-tight">Send us a message</h3>
      <p className="mt-1.5 font-body text-sm text-muted-foreground">We reply within one business day.</p>

      {/* Honeypot */}
      <div aria-hidden="true" className="absolute left-[-9999px] top-auto h-0 w-0 overflow-hidden">
        <label>
          Company website
          <input tabIndex={-1} autoComplete="off" data-testid="contact-form-hp" value={hp} onChange={(e) => setHp(e.target.value)} />
        </label>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="cf-name" className={FIELD_LABEL}>Name</Label>
          <Input id="cf-name" data-testid="contact-form-name" required minLength={2}
            value={form.name} onChange={set("name")} placeholder="Avery Lin" className="mt-2" />
        </div>
        <div>
          <Label htmlFor="cf-company" className={FIELD_LABEL}>Company</Label>
          <Input id="cf-company" data-testid="contact-form-company" required minLength={2}
            value={form.company} onChange={set("company")} placeholder="Northwind Health" className="mt-2" />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="cf-email" className={FIELD_LABEL}>Work email</Label>
          <Input id="cf-email" type="email" data-testid="contact-form-email" required
            value={form.email} onChange={set("email")} placeholder="avery@northwind.com" className="mt-2" />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="cf-interest" className={FIELD_LABEL}>I'm here to</Label>
          <select
            id="cf-interest"
            data-testid="contact-form-interest"
            value={form.interest}
            onChange={set("interest")}
            className="mt-2 h-10 w-full rounded-md border border-input bg-background px-3 text-sm outline-none transition-colors focus:border-sema-mid"
          >
            {INTERESTS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="cf-message" className={FIELD_LABEL}>What do you want to answer?</Label>
          <Textarea id="cf-message" data-testid="contact-form-message" required minLength={10} rows={3}
            value={form.message} onChange={set("message")}
            placeholder="E.g. Which facilities are above target on 30-day readmissions?" className="mt-2" />
        </div>
      </div>

      {error && <p data-testid="contact-form-error" className="mt-4 text-sm text-destructive">{error}</p>}

      <div className="mt-6 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
          <ShieldCheck className="h-3.5 w-3.5" /> Spam-protected · no marketing lists
        </p>
        <Button
          type="submit"
          data-testid="contact-form-submit"
          size="lg"
          disabled={pending}
          className="rounded-full sema-cta-gradient px-6 font-mono text-xs uppercase tracking-[0.2em] text-white shadow-lg shadow-sema-primary/30 transition-transform hover:-translate-y-0.5"
        >
          {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Send message <Check className="ml-2 h-4 w-4" /></>}
        </Button>
      </div>
    </form>
  );
}

export function ContactSection() {
  const reduce = useReducedMotion();
  return (
    <section id="contact" data-testid="landing-contact" className="relative w-full border-t border-border/60">
      <div className="mx-auto grid w-full max-w-6xl grid-cols-1 gap-12 px-6 py-20 sm:py-28 lg:grid-cols-2 lg:gap-16">
        <motion.div
          initial={reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          <div className="mb-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-accent" />
            {CONTACT_SECTION.eyebrow}
          </div>
          <h2 className="font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            {CONTACT_SECTION.title}
          </h2>
          <p className="mt-5 max-w-md font-body text-base leading-relaxed text-muted-foreground sm:text-lg">
            {CONTACT_SECTION.subtitle}
          </p>
          <ul className="mt-8 space-y-3">
            {CONTACT_SECTION.points.map((p) => (
              <li key={p} className="flex items-start gap-3 font-body text-sm text-foreground/90">
                <span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-sema-accent/15 text-sema-accent">
                  <Check className="h-3 w-3" strokeWidth={3} />
                </span>
                {p}
              </li>
            ))}
          </ul>
        </motion.div>

        <motion.div
          initial={reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.55, ease: "easeOut", delay: 0.1 }}
        >
          <SalesContactForm />
        </motion.div>
      </div>
    </section>
  );
}
